import { useState, useEffect } from 'react';
import { listAllUsers, updateUserSubscription, adminDeleteUser, adminConfirmUser, AdminUserInfo } from '@/api/admin';
import { adminSetEmailPrefs, adminGetAllEmailPrefs } from '@/api/game';
import { fetchAuthSession } from 'aws-amplify/auth';
import Button from '@/components/common/Button';

const ADMIN_EMAILS = ['diegotuleski@gmail.com', 'benjamintuleski@gmail.com'];

const TIERS = ['FREE', 'LIGHT', 'STANDARD', 'PREMIUM'];

const SubscriptionsPage = () => {
  const [users, setUsers] = useState<AdminUserInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTier, setFilterTier] = useState<string>('ALL');
  const [updatingUserId, setUpdatingUserId] = useState<string | null>(null);
  const [emailPrefs, setEmailPrefs] = useState<Map<string, boolean>>(new Map());

  useEffect(() => {
    const checkAdmin = async () => {
      try {
        const session = await fetchAuthSession();
        const email = session.tokens?.idToken?.payload?.email as string;
        if (!ADMIN_EMAILS.includes(email)) {
          setError('Unauthorized: Admin access only');
          setLoading(false);
          return;
        }
        setIsAdmin(true);
        await loadUsers();
      } catch (err) {
        console.error('Failed to check admin status:', err);
        setError('Failed to verify admin access');
        setLoading(false);
      }
    };
    checkAdmin();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const [response, prefs] = await Promise.allSettled([
        listAllUsers({ pageSize: 1000 }),
        adminGetAllEmailPrefs(),
      ]);
      if (response.status === 'fulfilled') setUsers(response.value.users);
      if (prefs.status === 'fulfilled') {
        const map = new Map<string, boolean>();
        prefs.value.forEach(p => map.set(p.userId, p.dailyDigest));
        setEmailPrefs(map);
      }
      setError(null);
    } catch (err) {
      console.error('Failed to load users:', err);
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleEmail = async (user: AdminUserInfo) => {
    const current = emailPrefs.get(user.userId) || false;
    const newVal = !current;
    try {
      await adminSetEmailPrefs(user.userId, user.email, user.username, newVal);
      setEmailPrefs(prev => { const m = new Map(prev); m.set(user.userId, newVal); return m; });
    } catch (err) {
      console.error('Failed to toggle email:', err);
      alert('Failed to update email preference');
    }
  };

  const handleUpdateSubscription = async (userId: string, tier: string, status: string) => {
    if (!confirm(`Update subscription for user to ${tier} (${status})?`)) {
      return;
    }

    try {
      setUpdatingUserId(userId);
      await updateUserSubscription({ userId, tier, status });
      
      // Update local state
      setUsers(users.map(u => 
        u.userId === userId 
          ? { ...u, tier, rateLimit: { ...u.rateLimit, tier } }
          : u
      ));
      
      alert('Subscription updated successfully!');
    } catch (err) {
      console.error('Failed to update subscription:', err);
      alert('Failed to update subscription');
    } finally {
      setUpdatingUserId(null);
    }
  };

  const handleDeleteUser = async (user: AdminUserInfo) => {
    if (!confirm(`Delete user "${user.username}" (${user.email})? This cannot be undone.`)) {
      return;
    }

    try {
      setUpdatingUserId(user.userId);
      await adminDeleteUser(user.userId);
      setUsers(users.filter(u => u.userId !== user.userId));
      alert('User deleted successfully');
    } catch (err) {
      console.error('Failed to delete user:', err);
      alert('Failed to delete user');
    } finally {
      setUpdatingUserId(null);
    }
  };

  const handleConfirmUser = async (user: AdminUserInfo) => {
    try {
      setUpdatingUserId(user.userId);
      await adminConfirmUser(user.email);
      setUsers(users.map(u => u.userId === user.userId ? { ...u, cognitoStatus: 'CONFIRMED' } : u));
      alert(`User ${user.email} confirmed!`);
    } catch (err) {
      console.error('Failed to confirm user:', err);
      alert('Failed to confirm user');
    } finally {
      setUpdatingUserId(null);
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = 
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.username.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesTier = filterTier === 'ALL' || user.tier === filterTier;
    return matchesSearch && matchesTier;
  });

  if (!isAdmin) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <h2 className="text-2xl font-bold text-red-800 mb-2">Access Denied</h2>
          <p className="text-red-600">{error || 'You do not have permission to access this page.'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-text-primary mb-2">Subscription Management</h1>
        <p className="text-text-secondary">Manage user subscription tiers and statuses</p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">
              Search by email or username
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search users..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">
              Filter by tier
            </label>
            <select
              value={filterTier}
              onChange={(e) => setFilterTier(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent"
            >
              <option value="ALL">All Tiers</option>
              {TIERS.map(tier => (
                <option key={tier} value={tier}>{tier}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Users Table */}
      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-blue"></div>
          <p className="mt-4 text-text-secondary">Loading users...</p>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <p className="text-red-600">{error}</p>
          <Button onClick={loadUsers} className="mt-4">Retry</Button>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                    Current Tier
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                    Rate Limit
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                    Games Played
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                    Email Digest
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredUsers.map((user) => (
                  <tr key={user.userId} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-text-primary">{user.username}</div>
                      <div className="text-sm text-text-secondary">{user.email}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        user.tier === 'PREMIUM' ? 'bg-purple-100 text-purple-800' :
                        user.tier === 'STANDARD' ? 'bg-blue-100 text-blue-800' :
                        user.tier === 'LIGHT' ? 'bg-green-100 text-green-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {user.tier}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-text-secondary">
                      {user.rateLimit.used} / {user.rateLimit.limit}
                      <div className="text-xs text-text-tertiary">
                        {user.rateLimit.remaining} remaining
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-text-secondary">
                      {user.gamesPlayed} total
                      <div className="text-xs text-text-tertiary">
                        {user.todayPlays} today
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => handleToggleEmail(user)}
                        className={`relative w-11 h-6 rounded-full transition-colors duration-200 ${
                          emailPrefs.get(user.userId) ? 'bg-green-500' : 'bg-gray-300'
                        }`}
                      >
                        <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${
                          emailPrefs.get(user.userId) ? 'translate-x-5' : 'translate-x-0'
                        }`} />
                      </button>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex gap-2 items-center">
                        <select
                          value={user.tier}
                          onChange={(e) => handleUpdateSubscription(user.userId, e.target.value, 'ACTIVE')}
                          disabled={updatingUserId === user.userId}
                          className="text-sm px-3 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent disabled:opacity-50"
                        >
                          {TIERS.map(tier => (
                            <option key={tier} value={tier}>{tier}</option>
                          ))}
                        </select>
                        {user.cognitoStatus !== 'CONFIRMED' && user.cognitoStatus !== 'EXTERNAL_PROVIDER' && (
                          <button
                            onClick={() => handleConfirmUser(user)}
                            disabled={updatingUserId === user.userId}
                            className="p-1.5 text-green-600 hover:text-green-800 hover:bg-green-50 rounded-lg transition-colors disabled:opacity-50"
                            title="Confirm user email"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </button>
                        )}
                        {user.tier === 'FREE' && (
                          <button
                            onClick={() => handleDeleteUser(user)}
                            disabled={updatingUserId === user.userId}
                            className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                            title="Delete user"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {filteredUsers.length === 0 && (
            <div className="text-center py-12">
              <p className="text-text-secondary">No users found matching your filters.</p>
            </div>
          )}
          
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
            <p className="text-sm text-text-secondary">
              Showing {filteredUsers.length} of {users.length} users
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionsPage;
